/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { useContext, useState, useRef } from 'react';
import { AuthContext } from '../../Contexts/AuthContext';
import useStaffAugmentationState from './useStaffAugmentationState';
import { STAFF_AUGMENTATION } from '../../Constants/strings';
import ActionButtons from './actionButton';
import { MODULE_NAMES } from '../../Constants/modules';
import SearchDropdown from '../../Components/Dropdown/dropdown';
import UseAxios from '../../Api/http/http';
import { getStaffingStatus } from '../../Api/request/staffAugStatusMaster';
import { useNavigate } from 'react-router-dom';
import {
  deleteStaffAugmentation,
  getStaffAugmentation,
  updateStaffAugmentation,
  getEmployeeNames,
  getEmployeeIds,
} from '../../Api/request/staffAugmentation';
import {
  customStatusStyles,
  formatDate,
  handleJDDownload,
} from './staffAugmentationUtils';
import { useSnackbar } from '../../Components/Snackbar/snackBarProvider';
import { ReactComponent as EyeIcon } from '../../Assets/images/svg/eye.svg';
import { ReactComponent as DownloadIcon } from '../../Assets/images/svg/dar_staff_download.svg';
import { getStaffAugDataExcel } from '../../Api/request/reports';
import { STATUS_CODES } from '../../Api/resources/statusCodes';
import { getViewFileData } from '../../Api/request/resourceRequest';
import { logError } from '../../Utils/logger';

const useStaffAugmentation = () => {
  const {
    rows,
    isMobileView,
    isLaptopView,
    isEdit,
    isCopy,
    currentPage,
    statusOptions,
    projectOptions,
    employeeNameOptions,
    employeeIdOptions,
    deleteStaffAugmentationDialog,
    staffAugmentationId,
    staffAugFilterValues,
    listStaffAugData,
    addRequirementOpen,
    editStaffData,
    anchorEl,
    isPdfHovered,
    isExcelHovered,
    jobDescView,
    jobDescFileUrl,
    isLoading,
    setRows,
    setIsMobileView,
    setIsLaptopView,
    setIsEdit,
    setIsCopy,
    setCurrentPage,
    setStatusOptions,
    setProjectOptions,
    setEmployeeNameOptions,
    setEmployeeIdOptions,
    setDeleteStaffAugmentationDialog,
    setStaffAugmentationId,
    setStaffAugFilterValues,
    setListStaffAugData,
    setAddRequirementOpen,
    setEditStaffData,
    setAnchorEl,
    setIsPdfHovered,
    setIsExcelHovered,
    setJobDescView,
    setJobDescFileUrl,
    setIsLoading,
  } = useStaffAugmentationState();
  const { userProfile } = useContext(AuthContext);
  const [severity, setSeverity] = useState('');
  const { snackbarOpen } = useSnackbar();
  const navigate = useNavigate();

  const permission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.STAFF_AUG_REQ_STATUS] || []
    : [];
  const clonePermission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.STAFF_AUG_CLONE_REQUIREMENT] || []
    : [];
  const resourcePermission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.STAFF_AUG_PROJECT_RESOURCING] || []
    : [];
  const checkCloneAccess = (action) => {
    return clonePermission.includes(action);
  };
  const checkAccess = (action) => {
    return permission.includes(action);
  };
  const checkResourceAccess = (action) => {
    return resourcePermission.includes(action);
  };

  const checkMobileView = () => {
    setIsMobileView(window.innerWidth < 1024);
    setIsLaptopView(window.innerWidth >= 1024 && window.innerWidth <= 2400);
  };

  const getColumnWidth = (mobile, laptop, defaultWidth) => {
    if (isMobileView) {
      return mobile;
    } else if (isLaptopView) {
      return laptop;
    }
    return defaultWidth;
  };

  const getColumnFlex = (defaultFlex) => {
    if (!isMobileView && !isLaptopView) {
      return defaultFlex;
    }
    return undefined;
  };

  const category = 'requirement-status';
  // API call to get staff augmentation status options
  const getStaffAugStatusData = () => {
    UseAxios(getStaffingStatus('', category, true))
      .then((response) => {
        const staffStatus = response.data.staffingStatusDetails.map(
          (statusDetails) => ({
            ...statusDetails,
            name: statusDetails.statusName,
          }),
        );
        setStatusOptions(staffStatus);
      })
      .catch((error) => {
        logError('Error in getStaffAugStatusData:', error);
        setStatusOptions([]);
      });
  };

  // API call to get employee names for dropdown
  const getEmployeeNameData = () => {
    UseAxios(getEmployeeNames())
      .then((response) => {
        if (response?.status === 200 && response?.data?.data) {
          const employeeNames = response.data.data.map((emp) => ({
            id: emp.employeeId,
            name: emp.employeeName,
          }));
          setEmployeeNameOptions(employeeNames);
        } else {
          setEmployeeNameOptions([]);
        }
      })
      .catch((error) => {
        logError('Error in getEmployeeNameData:', error);
        setEmployeeNameOptions([]);
      });
  };

  // API call to get employee IDs for dropdown
  const getEmployeeIdData = () => {
    UseAxios(getEmployeeIds())
      .then((response) => {
        if (response?.status === 200 && response?.data?.data) {
          const employeeIds = response.data.data.map((emp) => ({
            id: emp.employeeId,
            name: emp.displayEmployeeId,
            fullName: emp.employeeName,
          }));
          setEmployeeIdOptions(employeeIds);
        } else {
          setEmployeeIdOptions([]);
        }
      })
      .catch((error) => {
        logError('Error in getEmployeeIdData:', error);
        setEmployeeIdOptions([]);
      });
  };

  // API call to get staff augmentation list data
  const getStaffAugReqData = () => {
    setIsLoading(true);
    const filters = {};

    // Add employee name filter
    if (staffAugFilterValues.employeeName && staffAugFilterValues.employeeName.length > 0) {
      filters.employeeName = staffAugFilterValues.employeeName.map(emp => emp.name).join(',');
    }

    // Add employee ID filter
    if (staffAugFilterValues.employeeId && staffAugFilterValues.employeeId.length > 0) {
      filters.employeeId = staffAugFilterValues.employeeId.map(emp => emp.name).join(',');
    }

    const isSuperAdmin = Boolean(
      userProfile?.roleDetails?.some(
        (role) => role.roleName === 'Super Admin' || role.role_name === 'Super Admin',
      ) || userProfile?.superAdmin,
    );
    const hasFullActionAccess = isSuperAdmin
      || permission.includes('create')
      || permission.includes('update')
      || permission.includes('delete');
    if (!hasFullActionAccess) {
      filters.is_team_visible = 1;
    }

    UseAxios(getStaffAugmentation(filters))
      .then((response) => {
        if (response?.status === 200) {
          setRows(response.data);
          setListStaffAugData(response.data);
        } else {
          setRows([]);
          setListStaffAugData([]);
        }
      })
      .catch((error) => {
        logError('Error in getStaffAugReqData:', error);
        setRows([]);
        setListStaffAugData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // API call to update staff augmentation data by id
  const updateStaffAugStatus = async (data) => {
    try {
      const response = await UseAxios(updateStaffAugmentation(data));
      if (response.status === 200) {
        snackbarOpen(
          STAFF_AUGMENTATION.UPDATE_SUCCESS_MESSAGE,
          STAFF_AUGMENTATION.SUCCESS_SEVERITY,
        );
      }
      if (response.data.status === 500) {
        throw new Error(STAFF_AUGMENTATION.UPDATE_ERROR_MESSAGE);
      }
    } catch (error) {
      logError('Error in updateStaffAugStatus:', error);
      snackbarOpen(
        STAFF_AUGMENTATION.UPDATE_ERROR_MESSAGE,
        STAFF_AUGMENTATION.ERROR_SEVERITY,
      );
    }
  };
  // To update handle status change
  const handleStatusChange = (rowId, selectedStatus) => {
    const formData = new FormData();
    formData.append('statusId', selectedStatus.id);
    formData.append('isStatusUpdate', true);
    formData.append('staffingId', rowId);
    updateStaffAugStatus(formData);
    const { statusName } = selectedStatus;
    setListStaffAugData((prevList) =>
      prevList.map((item) =>
        item.id === rowId ? { ...item, statusName: statusName } : item,
      ),
    );
  };

  const handleDeleteClick = (params) => {
    setDeleteStaffAugmentationDialog(true);
    setStaffAugmentationId(params.id);
  };

  const handleEditClick = (params) => {
    setAddRequirementOpen(true);
    setIsEdit(true);
    setEditStaffData(params);
  };

  const handleCopyClick = (params) => {
    setAddRequirementOpen(true);
    setIsCopy(true);
    setEditStaffData(params);
  };

  // API call to delete staff Augmentation
  const handleDeleteStaffAugmentation = async () => {
    try {
      const payload = {
        staffingId: staffAugmentationId,
        lastUpdatedBy: userProfile?.user_name,
      };
      const response = await UseAxios(
        deleteStaffAugmentation(payload),
      );
      if (response.status === 200) {
        getStaffAugReqData();
        snackbarOpen(
          STAFF_AUGMENTATION.DELETE_SUCCESS_MESSAGE,
          STAFF_AUGMENTATION.DELETE_SUCCESS_SEVERITY,
        );
      }
      if (response.data.status === 500) {
        throw new Error(STAFF_AUGMENTATION.DELETE_ERROR_MESSAGE);
      }
      if (response.data?.status === STATUS_CODES.INVALID_REQUEST
         && response.data?.response?.data?.isMapped) {
        snackbarOpen(
          STAFF_AUGMENTATION.RESOURCE_MAPPED,
          STAFF_AUGMENTATION.DELETE_ERROR_SEVERITY,
          6000,
        );
      }
    } catch (error) {
      logError('Error in handleDeleteStaffAugmentation:', error);
      snackbarOpen(
        STAFF_AUGMENTATION.DELETE_ERROR_MESSAGE,
        STAFF_AUGMENTATION.DELETE_ERROR_SEVERITY,
      );
    } finally {
      setDeleteStaffAugmentationDialog(false);
    }
  };

  const handleOpenJobDescView = () => setJobDescView(true);

  const handleDownloadExcel = async () => {
    try {
      await getStaffAugDataExcel();
    } catch (error) {
      logError('Error in handleDownloadExcel:', error);
      snackbarOpen(
        STAFF_AUGMENTATION.DOWNLOAD_FAILURE_MESSAGE,
        STAFF_AUGMENTATION.ERROR_SEVERITY,
      );
    } finally {
      handleCloseMenu();
    }
  };
  const renderBooleanCell = (value) => <div>{value ? 'Yes' : 'No'}</div>;

  const columns = [
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.OPPORTUNITY,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.OPPORTUNITY,
      width: getColumnWidth(200, 200, 230),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
      hideCoumnMenuIcon: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.VERTICAL,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.VERTICAL,
      width: getColumnWidth(200, 150, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.LOCATION,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.LOCATION,
      width: getColumnWidth(200, 150, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.SKILLS,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.SKILLS,
      width: getColumnWidth(200, 150, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.YEAR_OF_EXP,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.YEAR_OF_EXP,
      width: getColumnWidth(200, 130, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
      multiLineHeader: true,
      hideCoumnMenuIcon: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.DURATION_MONTHS,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.DURATION_MONTHS,
      width: getColumnWidth(200, 130, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
      multiLineHeader: true,
      hideCoumnMenuIcon: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.RAISED_DATE,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.RAISED_DATE,
      width: getColumnWidth(200, 160, 190),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => formatDate(params),
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.JD_RECEIVED,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.JD_RECEIVED,
      width: getColumnWidth(200, 130, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const handleDownloadWithErrorHandling = async (staffingId) => {
          try {
            await handleDownloadJdFile(staffingId);
          } catch (error) {
            snackbarOpen(
              'Error downloading job description',
              STAFF_AUGMENTATION.ERROR_SEVERITY,
            );
          }
        };

        return (
          <div className="staff-augmentation-jd-received">
            {params.value === true ? 'Yes' : 'No'}{' '}
            {params.value === true && params.row?.jdDetails?.fileName && (
              <>
                {params.row.jdDetails.fileName
                  .toLowerCase()
                  .endsWith('.pdf') ? (
                    <button
                      onClick={() => {
                        getViewJdData(params.row.id);
                      }}
                    >
                      <EyeIcon />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDownloadWithErrorHandling(params.row.id)}
                    >
                      <DownloadIcon />
                    </button>
                  )}
              </>
            )}
          </div>
        );
      },
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.DEAL_TYPE,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.DEAL_TYPE,
      width: getColumnWidth(165, 150, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.GEOGRAPHY,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.GEOGRAPHY,
      width: getColumnWidth(165, 150, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.SALES_MANAGER,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.SALES_MANAGER,
      width: getColumnWidth(200, 180, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.NO_OF_POSITIONS,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.NO_OF_POSITIONS,
      width: getColumnWidth(200, 200, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.CLOSED_POSITIONS,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.CLOSED_POSITIONS,
      width: getColumnWidth(200, 200, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.PENDING_POSITIONS,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.PENDING_POSITIONS,
      width: getColumnWidth(200, 200, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.DELIVERY_MANAGER,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.DELIVERY_MANAGER,
      width: getColumnWidth(200, 200, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.RESOURCE_ONBOARDED,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.RESOURCE_ONBOARDED,
      width: getColumnWidth(200, 120, 220),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
      multiLineHeader: true,
      hideCoumnMenuIcon: true,
      renderCell: (params) => renderBooleanCell(params.value),
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.INTERVIEW_REQUIRED,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.INTERVIEW_REQUIRED,
      width: getColumnWidth(200, 120, 220),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
      multiLineHeader: true,
      renderCell: (params) => renderBooleanCell(params.value),
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.CLOSURE_DATE,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.CLOSURE_DATE,
      width: getColumnWidth(200, 160, 220),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => formatDate(params),
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.COMMENTS,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.COMMENTS,
      width: getColumnWidth(250, 250, 250),
      flex: getColumnFlex(1.5),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.LOST_DROPPED_TYPE,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.LOST_DROPPED_TYPE,
      width: getColumnWidth(250, 250, 250),
      flex: getColumnFlex(1.5),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.LOST_DROPPED_REASON,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.LOST_DROPPED_REASON,
      width: getColumnWidth(250, 250, 250),
      flex: getColumnFlex(1.5),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: STAFF_AUGMENTATION.STAFF_AUG_FIELD.STATUS,
      headerName: STAFF_AUGMENTATION.STAFF_AUG_HEADERNAME.STATUS,
      width: getColumnWidth(200, 200, 200),
      flex: getColumnFlex(0.7),
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const getStatusColour
          = statusOptions.find((option) => option.name === params.value)
            ?.statusColor || '';
        return (
          <div className="staff-augmentation-status-dropdown">
            <SearchDropdown
              options={statusOptions}
              value={params.value}
              searchBox={false}
              onChange={(event) => {
                if (!checkAccess('update')) return;
                handleStatusChange(params.id, event.target.value);
              }}
              customCss={customStatusStyles(getStatusColour)}
              id="staff-augmentation-status-dropdown"
              disabled={!checkAccess('update')}
            />
          </div>
        );
      },
    },
  ];

  const fixedColumns = [
    ...columns,
    {
      field: 'action',
      headerName: 'Actions',
      headerClassName: 'action-column',
      cellClassName: 'action-cell',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <ActionButtons
          params={params}
          hasAccess={checkAccess}
          // handleSwitchChange={() => {}}
          hasCloneAccess={checkCloneAccess}
          hasResourceAccess={checkResourceAccess}
          handleEditStaffAugStatusClick={handleEditClick}
          handleCopyClick={handleCopyClick}
          handleDeleteClick={handleDeleteClick}
          handleRedirectToResource={handleRedirectToResource}
        />
      ),
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const sortFilterColumns = columns
    .filter(
      (column) => column.field !== STAFF_AUGMENTATION.STAFF_AUG_FIELD.STATUS,
    )
    .map((column) => column.field);
  // to filter staff augmentation data
  const handleStaffAugFilterValues = (
    newValue,
    field = 'project' | 'status',
  ) => {
    setStaffAugFilterValues((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  const handleRedirectToResource = (row) => {
    const selectedRowData = {
      skillsRequired: row[STAFF_AUGMENTATION.STAFF_AUG_FIELD.SKILLS],
      noOfPositions: row[STAFF_AUGMENTATION.STAFF_AUG_FIELD.NO_OF_POSITIONS],
      interviewRequired:
        row[STAFF_AUGMENTATION.STAFF_AUG_FIELD.INTERVIEW_REQUIRED],
      empExperience: row[STAFF_AUGMENTATION.STAFF_AUG_FIELD.YEAR_OF_EXP],
      row,
    };
    navigate('/staff-augmentation/requirement-resource', {
      state: selectedRowData,
    });
  };

  const handleCloseMenu = () => {
    // handle close action on download reports
    setAnchorEl(null);
  };
  const handleDownloadClick = (event) => {
    // handle download reports
    setAnchorEl(event.currentTarget);
  };

  const handleCloseJobDescView = () => {
    setJobDescView(false);
    setJobDescFileUrl('');
  };

  const jdUrlCacheRef = useRef({});

  const getViewJdData = async (staffingId) => {
    try {
      if (jdUrlCacheRef.current[staffingId]) {
        setJobDescFileUrl(jdUrlCacheRef.current[staffingId]);
        handleOpenJobDescView();
        return;
      }
      const response = await UseAxios(getViewFileData(undefined, staffingId, true));
      if (response?.data?.jdUrl) {
        jdUrlCacheRef.current[staffingId] = response.data.jdUrl;
        setJobDescFileUrl(response.data.jdUrl);
        handleOpenJobDescView();
      }
    } catch (error) {
      logError('Error in getViewJdData:', error);
      snackbarOpen(
        'error fetching job description',
      );
    }
  };

  const handleDownloadJdFile = async (staffingId) => {
    try {
      const response = await UseAxios(getViewFileData(undefined, staffingId, true));
      if (response?.data?.jdUrl) {
        handleJDDownload(response.data.jdUrl);
      }
    } catch (error) {
      logError('Error in handleDownloadJdFile:', error);
      snackbarOpen(
        'Error downloading job description',
      );
    }
  };


  return {
    rows,
    isMobileView,
    isLaptopView,
    isEdit,
    isCopy,
    currentPage,
    severity,
    statusOptions,
    projectOptions,
    employeeNameOptions,
    employeeIdOptions,
    staffAugFilterValues,
    listStaffAugData,
    addRequirementOpen,
    editStaffData,
    anchorEl,
    isPdfHovered,
    isExcelHovered,
    jobDescView,
    jobDescFileUrl,
    isLoading,
    setIsEdit,
    setIsCopy,
    setRows,
    setCurrentPage,
    setProjectOptions,
    setListStaffAugData,
    checkMobileView,
    getColumnWidth,
    getColumnFlex,
    checkAccess,
    checkCloneAccess,
    checkResourceAccess,
    fixedColumns,
    sortFilterColumns,
    getStaffAugStatusData,
    getEmployeeNameData,
    getEmployeeIdData,
    getStaffAugReqData,
    deleteStaffAugmentationDialog,
    setDeleteStaffAugmentationDialog,
    handleDeleteStaffAugmentation,
    handleStaffAugFilterValues,
    setSeverity,
    setAddRequirementOpen,
    setEditStaffData,
    setIsPdfHovered,
    setIsExcelHovered,
    handleCloseMenu,
    handleDownloadClick,
    handleCloseJobDescView,
    handleDownloadExcel,
  };
};

export default useStaffAugmentation;
